import {
  HttpError,
  useCreate,
  useCustom,
  useTranslate,
  useUpdate,
} from "@refinedev/core";
import { useCallback, useMemo, useState } from "react";
import { Chapter, TemplateType } from "../types";
import { isEqual } from "lodash";
import {
  CreateMediaGenerationSettingsRequest,
  MediaGenerationSettingsResponse,
} from "pages/media/types/";
import { calculateMediaAspectRatio } from "../helpers";
import {
  DEFAULT_MEDIA_VIDEO_ASPECT_RATIO,
  MEDIA_HEGHT_BY_ASPECT_RATIO,
} from "pages/media/constants/";
import { ENABLE_CLOSING_KEY, ENABLE_INTRO_KEY } from "../constants";
import { useQueryClient } from "@tanstack/react-query";
import { useProjectInstance } from "pages/media/projects/hooks/useProjectInstance";

type ErrorWithStatus = HttpError & {
  response?: {
    status?: number;
  };
};

const isNotFound = (error: ErrorWithStatus | null) => {
  const status = error?.response?.status ?? error?.statusCode;
  return status === 404;
};

// Fallback used when a project has no media generation settings row yet.
const BASE_MEDIA_SETTINGS: Pick<
  CreateMediaGenerationSettingsRequest,
  "chapters" | "width" | "height"
> = {
  chapters: [],
  width: null,
  height: null,
};

export const useMediaGenerationSettings = ({
  projectId,
  organizationId,
  instanceId,
}: {
  projectId?: string;
  organizationId?: string;
  instanceId?: string;
}) => {
  const { mutateAsync: create, isPending: isCreatingSettings } =
    useCreate<MediaGenerationSettingsResponse>();
  const { mutateAsync: update, isPending: isUpdatingSettings } =
    useUpdate<MediaGenerationSettingsResponse>();
  const queryClient = useQueryClient();
  // BE only auto-marks the media stale on a Slides asset PATCH, never on a
  // settings write — yet these settings are the source of truth for the media
  // build, so flag it ourselves here.
  const { markMediaOutOfDate } = useProjectInstance({
    projectId: projectId ?? "",
    instanceId: instanceId ?? "",
  });

  const t = useTranslate();

  const {
    data: mediaSettingsResponse,
    isLoading: isMediaSettingsLoading,
    error,
    isError,
  } = useCustom<MediaGenerationSettingsResponse>({
    url: `media/${organizationId}/projects/${projectId}/media/${instanceId}/settings`,
    method: "get",
    queryOptions: {
      enabled: !!projectId && !!organizationId && !!instanceId,
      queryKey: ["ProjectMediaSettings", projectId],
      staleTime: Infinity,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: (failureCount, error) => {
        if (isNotFound(error)) {
          return false;
        }
        return failureCount < 3;
      },
    },
    // there might be no media settings for new projects
    errorNotification: false,
  });

  const isMediaSettingsError = isError && !isNotFound(error);

  const createMediaGenerationSettings = useCallback(
    async ({
      mediaSettings,
      successNotification = true,
    }: {
      mediaSettings: CreateMediaGenerationSettingsRequest;
      successNotification?: boolean;
    }) => {
      try {
        const response = await create({
          resource: `media/${organizationId}/projects/${projectId}/media/${instanceId}/settings`,
          values: mediaSettings,
          successNotification: successNotification
            ? () => ({
                message: t(
                  "components.templates.templatePreview.saveSettingsSuccess"
                ),
                type: "success",
              })
            : false,
        });
        queryClient.setQueryData(["ProjectMediaSettings", projectId], {
          data: response.data,
        });
        await markMediaOutOfDate();
        return response;
      } catch (e) {
        console.debug(e);
        throw e;
      }
    },
    [
      create,
      organizationId,
      projectId,
      instanceId,
      t,
      queryClient,
      markMediaOutOfDate,
    ]
  );

  const mediaGenerationSettings = useMemo(
    () => mediaSettingsResponse?.data,
    [mediaSettingsResponse?.data]
  );

  const getMediaGenerationSettingsTemplates = useCallback(
    (
      templateType: TemplateType
    ): {
      sectionId: string;
      chapterId: string;
    }[] => {
      const settingName = templateType.toLowerCase();
      if (
        !mediaGenerationSettings ||
        templateType === TemplateType.INTRODUCTION ||
        templateType === TemplateType.CLOSING
      )
        return [];

      return mediaGenerationSettings.chapters
        .map((chapter) => ({
          sectionId: chapter.chapter_id,
          chapterId:
            chapter[
              `${settingName}_template_id` as
                | "chapter_template_id"
                | "transition_template_id"
            ] ?? null,
        }))
        .filter(
          (chapter): chapter is { sectionId: string; chapterId: string } =>
            chapter.chapterId !== null
        );
    },
    [mediaGenerationSettings]
  );

  const generateMediaGenerationSettings = useCallback(
    ({
      currentChapter,
      presenterId,
    }: {
      currentChapter: Chapter;
      presenterId?: string;
    }) => {
      const baseMediaSettings = mediaGenerationSettings ?? BASE_MEDIA_SETTINGS;

      const currentChapterObject = baseMediaSettings.chapters.find(
        (chapter) => chapter.chapter_id === currentChapter.sectionId
      );

      const generateChapters = () => {
        const templateTypeLowerCase = currentChapter.type.toLowerCase();
        if (currentChapterObject) {
          return baseMediaSettings.chapters.map((chapter) =>
            chapter.chapter_id === currentChapter.sectionId
              ? {
                  ...chapter,
                  [`${templateTypeLowerCase}_template_id`]:
                    currentChapter.templateId,
                  ...(presenterId && {
                    presenter_id: presenterId,
                  }),
                }
              : chapter
          );
        } else {
          const newChapter = {
            chapter_id: currentChapter.sectionId,
            [`${templateTypeLowerCase}_template_id`]: currentChapter.templateId,
            ...(presenterId && { presenter_id: presenterId }),
          };

          return [...baseMediaSettings.chapters, newChapter];
        }
      };

      switch (currentChapter.type) {
        case TemplateType.CHAPTER:
        case TemplateType.TRANSITION:
          if (!currentChapter.templateId) return baseMediaSettings;
          return {
            ...baseMediaSettings,
            chapters: generateChapters(),
          };
        case TemplateType.INTRODUCTION:
          return {
            ...baseMediaSettings,
            introduction_template_id: currentChapter.templateId,
          };
        case TemplateType.CLOSING:
          return {
            ...baseMediaSettings,
            closing_template_id: currentChapter.templateId,
          };
        default:
          return baseMediaSettings;
      }
    },
    [mediaGenerationSettings]
  );

  const updateMediaGenerationSettings = useCallback(
    async ({
      updatedMediaSettings,
      successNotification = true,
      errorNotification = true,
    }: {
      updatedMediaSettings: Partial<CreateMediaGenerationSettingsRequest>;
      successNotification?: boolean;
      errorNotification?: boolean;
    }) => {
      // do not request for updateMediaGenerationSettings if the settings didn't change
      if (
        !projectId ||
        !organizationId ||
        !instanceId ||
        isEqual(updatedMediaSettings, mediaGenerationSettings)
      )
        return;

      try {
        const response = await update({
          resource: `media/${organizationId}/projects`,
          id: `${projectId}/media/${instanceId}/settings`,
          values: updatedMediaSettings,
          successNotification: successNotification
            ? () => {
                return {
                  message: t("components.templates.templatePreview.savedAll"),
                  type: "success",
                };
              }
            : false,
          errorNotification: errorNotification
            ? () => {
                return {
                  message: t("components.templates.templatePreview.saveError"),
                  type: "error",
                };
              }
            : false,
        });
        queryClient.setQueryData(["ProjectMediaSettings", projectId], {
          data: response.data,
        });
        await markMediaOutOfDate();
      } catch (e) {
        console.error("Error updating media generation settings", e);
        throw e;
      }
    },
    [
      update,
      projectId,
      organizationId,
      instanceId,
      t,
      mediaGenerationSettings,
      queryClient,
      markMediaOutOfDate,
    ]
  );

  const saveMediaGenerationSettings = useCallback(
    async (updatedSettings: CreateMediaGenerationSettingsRequest) => {
      try {
        if (mediaGenerationSettings) {
          await updateMediaGenerationSettings({
            updatedMediaSettings: updatedSettings,
            successNotification: false,
            errorNotification: false,
          });
        } else {
          await createMediaGenerationSettings({
            mediaSettings: updatedSettings,
          });
        }
      } catch (error) {
        console.error("Error saving media generation settings", error);
      }
    },
    [
      createMediaGenerationSettings,
      updateMediaGenerationSettings,
      mediaGenerationSettings,
    ]
  );

  const setChapterPresenter = useCallback(
    async (sectionId: string, presenterId: string | null): Promise<void> => {
      // A real chapter id is required. Without it we'd append a bogus chapter
      // row (empty chapter_id, null template ids) — this happens when the
      // avatar is changed while a slide (not a chapter) is selected, which
      // leaves currentChapter.sectionId empty.
      if (!sectionId) return;

      const baseMediaSettings = mediaGenerationSettings ?? BASE_MEDIA_SETTINGS;
      // Drop any previously-persisted bogus rows with an empty chapter_id.
      const validChapters = baseMediaSettings.chapters.filter(
        (chapter) => chapter.chapter_id
      );
      const existing = validChapters.find(
        (chapter) => chapter.chapter_id === sectionId
      );
      const updatedChapters = existing
        ? validChapters.map((chapter) =>
            chapter.chapter_id === sectionId
              ? { ...chapter, presenter_id: presenterId ?? undefined }
              : chapter
          )
        : [
            ...validChapters,
            {
              chapter_id: sectionId,
              ...(presenterId && { presenter_id: presenterId }),
            },
          ];
      await saveMediaGenerationSettings({
        ...baseMediaSettings,
        chapters: updatedChapters,
      });
    },
    [mediaGenerationSettings, saveMediaGenerationSettings]
  );

  const setAllChaptersPresenter = useCallback(
    async (presenterId: string | null, sectionIds: string[]): Promise<void> => {
      const baseMediaSettings = mediaGenerationSettings ?? BASE_MEDIA_SETTINGS;
      const sectionIdSet = new Set(sectionIds);
      const existingIds = new Set(
        baseMediaSettings.chapters.map((chapter) => chapter.chapter_id)
      );

      // Patch existing rows for the given sections; drop any bogus rows with an
      // empty chapter_id.
      const patchedExisting = baseMediaSettings.chapters
        .filter((chapter) => chapter.chapter_id)
        .map((chapter) =>
          sectionIdSet.has(chapter.chapter_id)
            ? { ...chapter, presenter_id: presenterId ?? undefined }
            : chapter
        );

      // Upsert a row for every section that doesn't have a settings row yet, so
      // chapters without a row still get the avatar.
      const newRows = sectionIds
        .filter((id) => !existingIds.has(id))
        .map((id) => ({
          chapter_id: id,
          ...(presenterId && { presenter_id: presenterId }),
        }));

      await saveMediaGenerationSettings({
        ...baseMediaSettings,
        chapters: [...patchedExisting, ...newRows],
      });
    },
    [mediaGenerationSettings, saveMediaGenerationSettings]
  );

  const setChapterTransitionVisibility = useCallback(
    async (sectionId: string, value: boolean): Promise<void> => {
      const baseMediaSettings = mediaGenerationSettings ?? BASE_MEDIA_SETTINGS;

      const existing = baseMediaSettings.chapters.find(
        (chapter) => chapter.chapter_id === sectionId
      );

      const updatedChapters = existing
        ? baseMediaSettings.chapters.map((chapter) =>
            chapter.chapter_id === sectionId
              ? { ...chapter, include_transition: value }
              : chapter
          )
        : [
            ...baseMediaSettings.chapters,
            { chapter_id: sectionId, include_transition: value },
          ];

      await saveMediaGenerationSettings({
        ...baseMediaSettings,
        chapters: updatedChapters,
      });
    },
    [mediaGenerationSettings, saveMediaGenerationSettings]
  );

  // Reorder the chapter settings rows to match the given section-id order so
  // generation reflects the storyboard's new chapter order. Rows whose id is
  // not in the list keep their relative order after the listed ones.
  const reorderChapters = useCallback(
    async (orderedSectionIds: string[]): Promise<void> => {
      // Reordering only makes sense once settings (with chapters) exist.
      if (!mediaGenerationSettings) return;
      const rank = new Map(orderedSectionIds.map((id, index) => [id, index]));
      const reorderedChapters = [...mediaGenerationSettings.chapters].sort(
        (a, b) =>
          (rank.get(a.chapter_id) ?? Number.MAX_SAFE_INTEGER) -
          (rank.get(b.chapter_id) ?? Number.MAX_SAFE_INTEGER)
      );

      // Use the mutation directly (not saveMediaGenerationSettings, which
      // swallows errors) so callers can detect a failed reorder and roll back.
      await updateMediaGenerationSettings({
        updatedMediaSettings: {
          ...mediaGenerationSettings,
          chapters: reorderedChapters,
        },
        successNotification: false,
        errorNotification: false,
      });
    },
    [mediaGenerationSettings, updateMediaGenerationSettings]
  );

  const setAllChaptersTransitionVisibility = useCallback(
    async (value: boolean, sectionIds: string[]): Promise<void> => {
      // Patch rows in place instead of rebuilding from sectionIds — if
      // sectionIds is briefly empty during a storyboard refetch, rebuilding
      // would wipe chapter_template_id / transition_template_id on every row.
      const baseMediaSettings = mediaGenerationSettings ?? BASE_MEDIA_SETTINGS;

      const sectionIdSet = new Set(sectionIds);
      const existingIds = new Set(
        baseMediaSettings.chapters.map((chapter) => chapter.chapter_id)
      );

      const patchedExisting = baseMediaSettings.chapters.map((chapter) =>
        sectionIdSet.has(chapter.chapter_id)
          ? { ...chapter, include_transition: value }
          : chapter
      );

      const newRows = sectionIds
        .filter((id) => !existingIds.has(id))
        .map((id) => ({ chapter_id: id, include_transition: value }));

      await saveMediaGenerationSettings({
        ...baseMediaSettings,
        chapters: [...patchedExisting, ...newRows],
      });
    },
    [mediaGenerationSettings, saveMediaGenerationSettings]
  );

  const setIntroClosingEnabled = useCallback(
    async (
      key: typeof ENABLE_INTRO_KEY | typeof ENABLE_CLOSING_KEY,
      value: boolean
    ): Promise<void> => {
      await saveMediaGenerationSettings({
        ...(mediaGenerationSettings ?? BASE_MEDIA_SETTINGS),
        [key]: value,
      });
    },
    [mediaGenerationSettings, saveMediaGenerationSettings]
  );

  const setMusicSettings = useCallback(
    async (
      includeMusic: boolean | null,
      musicAssetId: string | null
    ): Promise<void> => {
      const updatedMediaSettings = {
        ...(mediaGenerationSettings ?? BASE_MEDIA_SETTINGS),
        include_music: includeMusic,
        music_asset_id: musicAssetId,
      };

      // Bypass saveMediaGenerationSettings (which swallows errors) so callers
      // can catch a failed write and roll back optimistic UI.
      if (mediaGenerationSettings) {
        await updateMediaGenerationSettings({
          updatedMediaSettings,
          successNotification: false,
          errorNotification: false,
        });
      } else {
        await createMediaGenerationSettings({
          mediaSettings: updatedMediaSettings,
          successNotification: false,
        });
      }
    },
    [
      mediaGenerationSettings,
      updateMediaGenerationSettings,
      createMediaGenerationSettings,
    ]
  );

  const mediaAspectRatio = useMemo(() => {
    if (!mediaGenerationSettings) return DEFAULT_MEDIA_VIDEO_ASPECT_RATIO;
    const { width, height } = mediaGenerationSettings;
    if (!width || !height) return DEFAULT_MEDIA_VIDEO_ASPECT_RATIO;
    return calculateMediaAspectRatio(width / height);
  }, [mediaGenerationSettings]);

  const [localAspectRatioOverride, setLocalAspectRatioOverride] =
    useState<number>();

  const changeMediaSettingsSizes = useCallback(
    (newAspectRatio: number) => {
      setLocalAspectRatioOverride(newAspectRatio);

      const height = MEDIA_HEGHT_BY_ASPECT_RATIO[newAspectRatio];
      if (!height) return;

      saveMediaGenerationSettings({
        ...(mediaGenerationSettings ?? { chapters: [] }),
        width: Math.round(height * newAspectRatio),
        height,
      }).catch(console.error);
    },
    [mediaGenerationSettings, saveMediaGenerationSettings]
  );

  return {
    mediaGenerationSettings,
    isMediaSettingsLoading,
    // True while any media-settings write (create or update) is in flight.
    // Consumers can use this to disable concurrent writes that would race.
    isMediaSettingsSaving: isCreatingSettings || isUpdatingSettings,
    updateMediaGenerationSettings,
    generateMediaGenerationSettings,
    saveMediaGenerationSettings,
    setChapterPresenter,
    setAllChaptersPresenter,
    setChapterTransitionVisibility,
    setAllChaptersTransitionVisibility,
    reorderChapters,
    setIntroClosingEnabled,
    setMusicSettings,
    getMediaGenerationSettingsTemplates,
    mediaAspectRatio: localAspectRatioOverride ?? mediaAspectRatio,
    changeMediaSettingsSizes,
    isMediaSettingsError,
    // Exposed so callers already reading `mediaGenerationSettingsData` off
    // TemplateDataProvider's context don't need their own useProjectInstance
    // call just to reach this (see MED-6969).
    markMediaOutOfDate,
  };
};
